import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, MapPin, ArrowRight, Shield } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.05, duration: 0.5 } }),
};

const CAT_ICONS = {
  "eat-jamaica": "Eat in Jamaica",
  "hotels-jamaica": "Hotels in Jamaica",
  "travel-world": "Travel the World",
  "food-spots": "Global Food Spots",
  "premium-parties": "Premium Parties",
};

export default function RecommendationsPage() {
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState("eat-jamaica");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/recommendation-categories`).then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/recommendations/${activeCategory}`).then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [activeCategory]);

  return (
    <div data-testid="recommendations-page" className="pt-20 min-h-screen bg-luxury-black">
      {/* Hero */}
      <section className="py-24 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial="hidden" animate="visible">
            <motion.p variants={fadeUp} custom={0} className="text-xs font-body font-medium uppercase tracking-[0.3em] text-gold mb-6">Curated Recommendations</motion.p>
            <motion.h1 variants={fadeUp} custom={1} className="font-heading text-5xl sm:text-6xl font-light text-white leading-none mb-6">
              The Aristocrats<br /><span className="gold-gradient-text">Top 50 Lists</span>
            </motion.h1>
            <motion.p variants={fadeUp} custom={2} className="text-white/60 font-body font-light text-base leading-relaxed max-w-2xl">
              Handpicked by our team and refined by guest feedback. These are the destinations, experiences, and venues that meet our standard of excellence.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Data Privacy Commitment */}
      <section className="border-y border-white/5 py-4 bg-luxury-charcoal">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center gap-3">
          <Shield size={16} className="text-gold flex-shrink-0" />
          <p className="text-white/40 text-xs font-body">
            Your feedback drives our recommendations. We never sell your personal information to third parties without your explicit consent.{" "}
            <Link to="/privacy" className="text-gold hover:text-gold-light underline">Learn more</Link>
          </p>
        </div>
      </section>

      {/* Category Tabs */}
      <section className="bg-luxury-black py-6 sticky top-20 z-30 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex gap-2 overflow-x-auto pb-2">
          {categories.map((cat) => (
            <button
              key={cat.id}
              data-testid={`cat-${cat.id}`}
              onClick={() => setActiveCategory(cat.id)}
              className={`px-5 py-2 text-sm font-body font-light whitespace-nowrap transition-all ${
                activeCategory === cat.id
                  ? "bg-gold text-black"
                  : "border border-white/10 text-white/60 hover:border-white/30 hover:text-white"
              }`}
            >
              {CAT_ICONS[cat.id] || cat.name} ({cat.count})
            </button>
          ))}
        </div>
      </section>

      {/* Items Grid */}
      <section className="py-12 bg-luxury-black">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {loading ? (
            <div className="text-gold font-body text-sm animate-pulse text-center py-12">Loading recommendations...</div>
          ) : items.length === 0 ? (
            <div className="text-white/40 font-body text-sm text-center py-12">No recommendations in this category yet.</div>
          ) : (
            <motion.div initial="hidden" animate="visible" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {items.map((item, i) => (
                <motion.div key={item.id} variants={fadeUp} custom={i}>
                  <Link
                    to={`/recommendations/${activeCategory}/${item.id}`}
                    data-testid={`rec-item-${item.id}`}
                    className="glass-card overflow-hidden group block hover:border-gold/30 transition-all"
                  >
                    {item.image_url && (
                      <div className="aspect-[16/10] overflow-hidden bg-luxury-surface">
                        <img src={item.image_url} alt={item.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                      </div>
                    )}
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-gold text-xs font-body font-medium">#{item.rank}</span>
                        {item.avg_rating > 0 && (
                          <div className="flex items-center gap-1">
                            <Star size={12} className="text-gold fill-gold" />
                            <span className="text-gold text-xs font-body">{item.avg_rating}</span>
                            <span className="text-white/30 text-xs font-body">({item.review_count})</span>
                          </div>
                        )}
                      </div>
                      <h3 className="text-white font-heading text-lg group-hover:text-gold transition-colors mb-2">{item.name}</h3>
                      {item.description && <p className="text-white/50 font-body font-light text-xs leading-relaxed mb-3 line-clamp-2">{item.description}</p>}
                      {item.location && (
                        <div className="flex items-center gap-1 text-white/30 text-xs font-body">
                          <MapPin size={12} /> {item.location}
                        </div>
                      )}
                      {item.tags?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-3">
                          {item.tags.slice(0, 3).map((t) => (
                            <span key={t} className="px-2 py-0.5 text-[10px] border border-white/10 text-white/40 font-body">{t}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  </Link>
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      </section>
    </div>
  );
}
